import React, { useEffect, useState } from 'react';

import produce from 'immer';
import { DropResult } from 'react-beautiful-dnd';
import { useQueryClient } from 'react-query';

import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import { ITableColumnState } from 'hooks/useTableCurrentState';

import { CustomizedButton } from 'components/common/CustomizeButton/CustomizedButton';
import { CustomizedModalBase } from 'components/common/CustomizedModal/CustomizedModalBase';
import classes from 'components/common/Modals/CustomTableColumns/customTableColumnsModal.module.scss';
import {
    filterColumnsVisibility,
    filterRowOptions,
    isAllColumnsSelected,
    mapper,
} from 'components/common/Modals/CustomTableColumns/customTableColumnsModal.utils';
import { DraggableList } from 'components/common/Modals/CustomTableColumns/draggableList';

import { reorder } from 'components/Dashboards/Project/Components/CustomDashboard/customDashboard.utils';
import {
    useMutationSetTableColumns,
    useMutationSetWidgetColumns,
    useQueryGetTableColumns,
} from 'components/Dashboards/Project/Components/CustomDashboard/queries/useQuerySelectedTableColumns';
import { IHashmap } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';

interface ICustomTableColumnsModal {
    showModal: boolean;
    onHideModal: () => void;
    widgetId?: string;
    componentKey: keyof IHashmap;
    tableInstance: any;
    initialState: ITableColumnState;
    columns: any[];
    onChangeVisibility?: React.Dispatch<React.SetStateAction<{}>>;
    onChangeOrder?: React.Dispatch<React.SetStateAction<string[]>>;
}

export type IStateItem = {
    id: string;
    Header: string;
    isVisible: boolean;
    isDisableReorder: boolean;
    isDisableVisibility: boolean;
    accessor: string;
};

/**
 * Modal for re-organizing and hidding/showing table columnOrder.
 * @param {boolean}  showModal - determines if this modal is visible.
 * @param {() => void}  onHideModal - hide modal function.
 * @param {boolean}  isLoading - determines if this table is loading.
 * @param tableInstance
 * @param {ITableColumnState} initialState - initial state of the table columns

 * @param columns
 * @returns  Modal
 * */

export const CustomTableColumnsModal = ({
    showModal,
    onHideModal,
    widgetId,
    componentKey,
    tableInstance,
    initialState,
    columns,
    onChangeVisibility,
    onChangeOrder,
}: ICustomTableColumnsModal) => {
    const [state, setState] = useState<IStateItem[]>([]);
    const { mutate: mutateSetTableColumns, isLoading: isLoadingSetTableColumns } = useMutationSetTableColumns();
    const { mutate: mutateSetWidgetColumns, isLoading: isLoadingSetWidgetColumns } = useMutationSetWidgetColumns();
    const { handleSnackBar } = useCustomSnackBar();
    const allSelected = isAllColumnsSelected(state);
    const selectDeselectTitle = !allSelected ? 'Select all' : 'De-select all';
    const queryClient = useQueryClient();
    const onDragEnd = ({ destination, source }: DropResult) => {
        // dropped outside the list
        if (!destination) return;
        const destinationColumn = state[destination.index];
        if (!destinationColumn || destinationColumn.isDisableReorder) return;
        const newItems = reorder(state, source.index, destination.index);
        setState(newItems);
    };

    const { data: tableData, isLoading: tableDataLoading } = useQueryGetTableColumns({ componentKey });

    useEffect(() => {
        if (!tableData && !tableDataLoading && tableInstance) {
            const tableColumns = tableInstance.allColumns;
            const defaultColumns = tableColumns.map(mapper);
            const hiddenColumns = defaultColumns.filter((item) => !item.isVisible).map((item) => item.id);
            const columnOrder = defaultColumns.map((item) => item.id);
            if (columnOrder.length > 0) {
                mutateSetTableColumns(
                    { columnOrder, hiddenColumns, componentKey },
                    {
                        onSuccess: () => {
                            tableInstance.setColumnOrder(columnOrder);
                            tableInstance.setHiddenColumns(hiddenColumns);
                            queryClient.invalidateQueries('getTableColumns');
                        },
                    },
                );
            }
        }
    }, [tableData, tableInstance]);

    useEffect(() => {
        const tableColumns = tableInstance.allColumns.filter(filterRowOptions);
        const defaultColumns = tableColumns.map(mapper);
        setState(defaultColumns);
    }, [tableInstance]);

    const handleEditTableColumns = () => {
        const table = tableInstance;
        const hiddenColumns = state.filter((item) => !item.isVisible).map((item) => item.id);
        const leftColumns = table.allColumns.filter((item) => item.id === 'select-col').map((item) => item.id);
        const rightColumns = table.allColumns.filter((item) => item.id === 'rowOptions').map((item) => item.id);
        const columnOrder = [...leftColumns, ...state.map((item) => item.id), ...rightColumns];

        mutateSetTableColumns(
            { columnOrder, hiddenColumns, componentKey },
            {
                onSuccess: () => {
                    table.setColumnOrder(columnOrder);
                    table.setHiddenColumns(hiddenColumns);
                    queryClient.invalidateQueries('getTableColumns');
                    onHideModal();
                    onChangeVisibility &&
                        onChangeVisibility(
                            state.reduce((obj, item) => {
                                return {
                                    ...obj,
                                    [item.id]: !hiddenColumns.includes(item.id),
                                };
                            }, {}),
                        );
                    onChangeOrder && onChangeOrder(columnOrder);
                },
                onError: (error) => {
                    DEFAULT_ERROR({ error, handleError: handleSnackBar });
                },
            },
        );
    };
    const handleEditWidgetColumns = () => {
        const table = tableInstance;
        const hiddenColumns = state.filter((item) => !item.isVisible).map((item) => item.id);
        const columnOrder = state.map((item) => item.id);

        mutateSetWidgetColumns(
            {
                columns: {
                    columnOrder,
                    hiddenColumns,
                },
                widgetId,
            },
            {
                onSuccess: () => {
                    table.setColumnOrder(columnOrder);
                    table.setHiddenColumns(hiddenColumns);
                    queryClient.invalidateQueries('getWidgetColumns');
                    onHideModal();
                },
                onError: (error) => {
                    DEFAULT_ERROR({ error, handleError: handleSnackBar });
                },
            },
        );
    };
    const onHideShowItem = (e, column) => {
        const newItems = produce(state, (draft) => {
            return draft.map((item: IStateItem) => {
                if (item.id === column.id) {
                    return {
                        ...item,
                        isVisible: !item.isVisible,
                    };
                }
                return item;
            });
        });
        setState(newItems);
    };

    const onReset = ({ state }) => {
        if (!state) return null;
        setState(
            filterColumnsVisibility({
                columns,
                visibilityCondition: (item) => {
                    return state.hiddenColumns ? !state.hiddenColumns.includes(item.id) : true;
                },
            }),
        );
    };

    const onCancel = () => {
        onReset({ state: tableInstance.state });
        onHideModal();
    };

    const handleSelectAll = () => {
        setState(
            filterColumnsVisibility({
                columns,
                visibilityCondition: (item) => (!item.isDisableVisibility ? !allSelected : true),
            }),
        );
    };

    const isLoading = isLoadingSetTableColumns || isLoadingSetWidgetColumns;

    return (
        <CustomizedModalBase isModalOpen={showModal} handleModalClose={onCancel}>
            <div className={classes.modalContainer}>
                <div className={classes.title}>Show, Hide and Reorder Columns</div>
                <div className={classes.subTitle}>
                    <div>Unselect the columns you'd like to hide.</div>
                    <div>Drag columns to reorder</div>
                </div>
                <div className={classes.selectDeselect}>
                    <span
                        data-testid={'select-deselect-all'}
                        className={classes.selectDeselectTitle}
                        onClick={handleSelectAll}
                    >
                        {selectDeselectTitle}
                    </span>
                </div>
                <DraggableList columns={state} onDragEnd={onDragEnd} onHideShowItem={onHideShowItem} />
                <div className={classes.footer}>
                    <div className={classes.container}>
                        <div>
                            <CustomizedButton
                                size={'large'}
                                color={'secondary'}
                                onClick={() => onReset({ state: initialState })}
                                className={classes.reset}
                            >
                                Reset
                            </CustomizedButton>
                        </div>
                        <div data-testid={'actions'} className={classes.actions}>
                            <CustomizedButton size={'large'} color={'secondary'} onClick={onCancel}>
                                Cancel
                            </CustomizedButton>

                            <CustomizedButton
                                size={'large'}
                                color={'primary'}
                                onClick={widgetId ? handleEditWidgetColumns : handleEditTableColumns}
                                isLoading={isLoading}
                                disabled={isLoading}
                            >
                                Save
                            </CustomizedButton>
                        </div>
                    </div>
                </div>
            </div>
        </CustomizedModalBase>
    );
};
